import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TagManager from "react-gtm-module";

import { Header } from "@industriousoffice/react-components";
import { User } from "@/api/user/userTypes";
import { getFullPath } from "@/router/utils/getFullPath";
import { authApi } from "@/api/auth/authApi";
import { LegacyUser, LegacyUserGroupingDto } from "@/api/user/legacyUserTypes";
import { legacyUserApi } from "@/api/user/legacyUserApi";
import { inventoryApi } from "@/api/inventory/inventoryApi";
import { signOut } from "@/utils/signout";
import { LOCALE_OPTIONS } from "@/i18n/types";
import { useLocale } from "@/hooks/useLocale";

import {
  isDecisionMaker,
  isVirtualMember,
  canAccessHotDeskFeature,
  canAccessFeatureToggle,
} from "../../utils/userPermissions";

interface NavigationHeaderProps {
  user?: User;
}

const REFER_A_FRIEND_URL =
  "https://www.industriousoffice.com/refer?utm_medium=raf&utm_source=referralrock&utm_campaign=portalbanner";

function NavigationHeader({ user }: NavigationHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [groupings, setGroupings] = useState<LegacyUserGroupingDto[]>([]);
  const [legacyUser, setLegacyUser] = useState<LegacyUser>();
  const [locale, setLocale] = useLocale();
  const [handbookUrl, setHandbookUrl] = useState<string | null>(null);

  useEffect(() => {
    inventoryApi
      .fetchUserHandbook()
      .then((data) => {
        setHandbookUrl(data.url);
      })
      .catch(() => {});
  }, []);

  const [canAccessMemberPortalClaim, setCanAccessMemberPortalClaim] =
    useState<boolean>(false);

  useEffect(() => {
    // currently not refreshed and only updates on mount
    authApi.getCognitoUser().then((cognitoUser) => {
      setCanAccessMemberPortalClaim(
        JSON.parse(cognitoUser["custom:client_access"] ?? "{}")
          .can_access_member_portal
      );
    });
  }, []);

  const fetchLegacyUser = async (userId: string) => {
    try {
      const data = await legacyUserApi.fetchUser(userId);
      setLegacyUser(data);
      const tagManagerArgs = {
        dataLayer: {
          lastLoginDate: data.lastLoggedInOn,
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  const fetchUserGroupings = async (userId: string) => {
    try {
      const data = await legacyUserApi.fetchUserGroupings(userId);
      setGroupings(data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  useEffect(() => {
    if (!user?._id) return;
    fetchUserGroupings(user._id);
    fetchLegacyUser(user._id);
  }, [user?._id]);

  const navigateToMpWeb = (path: string) => () => {
    window.location.href = new URL(
      path,
      import.meta.env.VITE_MEMBER_PORTAL_URL
    ).toString();
  };

  const onReferAFriend = () => {
    window.open(REFER_A_FRIEND_URL);
  };

  const onSignOut = async () => {
    signOut();
    window.location.href = new URL(
      "/login",
      import.meta.env.VITE_MEMBER_PORTAL_URL
    ).toString();
  };

  const {
    isVirtual,
    canAccessMP,
    canAccessHotDesk,
    isDM,
    canAccessMemberManagement,
  } = useMemo(
    (): {
      isVirtual: boolean;
      canAccessMP: boolean;
      canAccessHotDesk: boolean;
      isDM: boolean;
      canAccessMemberManagement: boolean;
    } => ({
      isVirtual: legacyUser != null && isVirtualMember(legacyUser),
      canAccessMP: canAccessMemberPortalClaim,
      canAccessHotDesk:
        legacyUser != null && canAccessHotDeskFeature(legacyUser, groupings),
      isDM: legacyUser != null && isDecisionMaker(legacyUser),
      canAccessMemberManagement:
        legacyUser != null &&
        isDecisionMaker(legacyUser) &&
        canAccessFeatureToggle(legacyUser, groupings, "MemberManagement"),
    }),
    [legacyUser, groupings, canAccessMemberPortalClaim]
  );
  const leftNavOptions = [
    // MP access required
    ...(canAccessMP
      ? [
          {
            title: t("Home"),
            onPress: navigateToMpWeb("home"),
          },
          {
            title: t("Reserve"),
            items: [
              {
                title: t("Meeting Room"),
                onPress: navigateToMpWeb("home/calendar/roombooking"),
              },
              ...(!isVirtual
                ? [
                    {
                      title: t("Office"),
                      onPress: navigateToMpWeb("home/reserve-office"),
                    },
                    {
                      title: t("Open Workspace"),
                      onPress: navigateToMpWeb("home/openworkspace"),
                    },
                  ]
                : []),
              ...(canAccessHotDesk && !isVirtual
                ? [
                    {
                      title: t("Desk"),
                      renderTitle: () => (
                        <span className="flex items-center">
                          {t("Desk")}
                          <span className="rounded-[15px] text-[#FDD344] border-[#FDD344] lg:text-teal-40 lg:border-teal-40 ml-2 text-xs px-2 border py-1 ">
                            {t("beta")}
                          </span>
                        </span>
                      ),
                      onPress: navigateToMpWeb("home/desk"),
                    },
                  ]
                : []),
            ],
          },
          {
            title: t("Menu & Events"),
            onPress: navigateToMpWeb("home/events"),
          },
          {
            title: t("Member Benefits"),
            onPress: navigateToMpWeb("home/member-benefits"),
          },
        ]
      : []),
    {
      title: t("Billing"),
      renderTitle: () => (
        <span className="inline-flex items-center">{t("Billing")}</span>
      ),
      onPress: () => navigate(getFullPath("/")),
    },
  ];
  const dropdownOption = {
    userName: `${user?.firstName || ""} ${user?.lastName || ""}`,
    items: [
      // mp access required
      ...(canAccessMP
        ? [
            {
              title: t("Your Profile"),
              onPress: navigateToMpWeb("home/yourprofile"),
            },
          ]
        : []),
      // DM only
      ...(isDM && !isVirtual && canAccessMP
        ? [
            {
              title: t("Company Profile"),
              onPress: navigateToMpWeb("home/companyprofile"),
            },
          ]
        : []),
      // DM with feature toggle only
      ...(canAccessMemberManagement && canAccessMP
        ? [
            {
              title: t("Member Management"),
              onPress: navigateToMpWeb("home/memberManagement"),
            },
          ]
        : []),
      {
        title: t("FAQ"),
        onPress: navigateToMpWeb("home/FAQ"),
      },
      {
        title: t("Notification Settings"),
        onPress: navigateToMpWeb("home/settings"),
      },
      // MP access required
      ...(canAccessMP
        ? [
            {
              title: t("Refer a Friend"),
              onPress: onReferAFriend,
            },
          ]
        : []),
      ...(handbookUrl != null
        ? [
            {
              title: t("Member Handbook"),
              onPress: () => window.open(handbookUrl, "_blank"),
            },
          ]
        : []),
      {
        title: t("Sign Out"),
        onPress: onSignOut,
      },
    ],
  };

  return (
    <Header
      leftNavItems={leftNavOptions}
      rightNavItems={[]}
      userDropdown={dropdownOption}
      headerClassName="font-verlagbook z-10"
      onLogoPress={() => navigate(getFullPath("/"))}
      languagesProps={{
        languages: LOCALE_OPTIONS,
        selectedLanguage: locale,
        onChange: setLocale,
      }}
    />
  );
}
export default NavigationHeader;
